import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, HelpCircle, MessageSquarePlus, List, BookOpen } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import ConnectionStatusBanner from '@/components/ConnectionStatusBanner';
import { CheckademicLogo } from '@/components/CheckademicLogo';
import { SchoolLogo } from '@/components/SchoolLogo';
import { ViewSwitcher } from '@/components/ViewSwitcher';
import { useAuthStore } from '@/stores/authStore';
import { useNameFormat } from '@/utils/nameFormat';
import type { AuthenticatedUser } from '@/stores/authStore';
import type { TenantConfig } from '@shared/types/tenant-config';

interface TenantInfo {
  id: string;
  subdomain: string;
  schoolName: string;
  timezone: string;
  settings: TenantConfig;
  allowedModules: {
    attendance: boolean;
    parentPortal: boolean;
    sms: boolean;
    messaging: boolean;
  };
}

interface HeaderProps {
  tenant: TenantInfo | null;
  user: AuthenticatedUser | null;
  onLogout: () => void;
  showHamburger?: boolean;
  onHamburgerClick?: () => void;
}

/**
 * Global Header Component
 *
 * Renders consistent header across all tenant layouts (admin, teacher, support_staff, parent).
 * Includes logo, school name, user info, and logout button.
 *
 * @param tenant - Tenant data (for school name and branding colors)
 * @param user - Current user (for display name and role)
 * @param onLogout - Callback for logout button
 * @param showHamburger - Whether to show hamburger menu button (mobile only)
 * @param onHamburgerClick - Callback for hamburger button click
 */
export const Header = ({ tenant, user, onLogout, showHamburger = false, onHamburgerClick }: HeaderProps) => {
  const navigate = useNavigate();
  const { isMobile } = useMediaQuery();
  const { t } = useTranslation('common');
  const { viewMode, setViewMode } = useAuthStore();
  const { formatStaffName, formatParentName } = useNameFormat();
  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isParent = user?.role === 'parent';

  // Format user's display name based on their role
  const formatUserName = (firstName: string, lastName: string) => {
    if (isParent) {
      return formatParentName(firstName, lastName);
    }
    return formatStaffName(firstName, lastName);
  };

  // Support dropdown state
  const [supportDropdownOpen, setSupportDropdownOpen] = useState(false);
  const supportDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (supportDropdownRef.current && !supportDropdownRef.current.contains(event.target as Node)) {
        setSupportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const headerStyle: React.CSSProperties = {
    backgroundColor: 'var(--surface-color)',
    borderBottom: '1px solid var(--border-color)',
    height: isMobile ? 'auto' : 'var(--header-height)',
    minHeight: isMobile ? 'auto' : 'var(--header-height)',
    padding: isMobile ? '0.75rem 0' : '0 0',
    paddingTop: isMobile ? `calc(0.75rem + var(--safe-area-inset-top))` : '0',
    paddingBottom: isMobile ? '0.75rem' : '0',
    display: 'flex',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  };

  const headerContentStyle: React.CSSProperties = {
    width: '100%',
    padding: isMobile ? '0 0.5rem' : '0 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: isMobile ? '0.5rem' : '1rem',
  };

  const logoSectionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '0.5rem' : '1rem',
  };

  const logoButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  };

  const schoolNameStyle: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontSize: isMobile ? '0.75rem' : '0.875rem',
  };

  const hamburgerButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0.5rem',
    minHeight: '40px',
    minWidth: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--primary-color)',
  };

  const userInfoStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '0.5rem' : '1rem',
  };

  const userNameStyle: React.CSSProperties = {
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const nameTextStyle: React.CSSProperties = {
    transition: 'text-decoration 0.2s',
  };

  const logoutButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: 'var(--primary-color)',
    border: '1px solid var(--primary-color)',
    padding: '0.5rem 1rem',
    borderRadius: 'var(--radius)',
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    minHeight: '30px',
  };

  const supportDropdownContainerStyle: React.CSSProperties = {
    position: 'relative',
  };

  const supportButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0.5rem',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius)',
    transition: 'all 0.2s ease',
  };

  const supportDropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    // Mobile: align right edge to prevent overflow; Desktop: center under button
    ...(isMobile
      ? { right: 0 }
      : { left: '50%', transform: 'translateX(-50%)' }),
    marginTop: '0.5rem',
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    minWidth: '180px',
    zIndex: 200,
    overflow: 'hidden',
  };

  const supportDropdownItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    color: 'var(--text-primary)',
    textDecoration: 'none',
    fontSize: '0.875rem',
    transition: 'background-color 0.2s',
    cursor: 'pointer',
  };

  return (
    <>
      <ConnectionStatusBanner />
      <header style={headerStyle}>
        <div style={headerContentStyle}>
          {/* LEFT SECTION: Logo + School Name */}
          <div style={logoSectionStyle}>
            {/* Hamburger menu button (mobile only, conditional) */}
            {isMobile && showHamburger && (
              <button style={hamburgerButtonStyle} onClick={onHamburgerClick} aria-label="Open menu">
                <Menu size={24} aria-hidden="true" />
              </button>
            )}

            {/* Logo - clickable, navigates to dashboard */}
            <button
              style={logoButtonStyle}
              onClick={() => navigate('/')}
              aria-label="Go to dashboard"
              title="Go to dashboard"
            >
              <CheckademicLogo
                height={isMobile ? '32px' : '40px'}
                primaryColor="var(--primary-color)"
                accentColor="var(--secondary-color)"
                textColor="var(--primary-color)"
              />
            </button>

            {/* School Name */}
            {tenant && <div style={schoolNameStyle}>{tenant.schoolName}</div>}

            {/* School Logo (if uploaded) */}
            {tenant && (
              <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="sm" />
            )}
          </div>

          {/* RIGHT SECTION: Support + User Info + Logout */}
          <div style={userInfoStyle}>
            {/* Support Dropdown (not for super admin or mobile - they have admin panel / bottom nav) */}
            {!isSuperAdmin && user && !isMobile && (
              <div ref={supportDropdownRef} style={supportDropdownContainerStyle}>
                <button
                  style={supportButtonStyle}
                  onClick={() => setSupportDropdownOpen(!supportDropdownOpen)}
                  aria-label="Support"
                  title="Support"
                >
                  <HelpCircle size={20} />
                </button>
                {supportDropdownOpen && (
                  <div style={supportDropdownStyle}>
                    <Link
                      to="/help"
                      style={supportDropdownItemStyle}
                      onClick={() => setSupportDropdownOpen(false)}
                    >
                      <BookOpen size={16} />
                      {t('nav.helpCenter')}
                    </Link>
                    <Link
                      to="/support/new"
                      style={supportDropdownItemStyle}
                      onClick={() => setSupportDropdownOpen(false)}
                    >
                      <MessageSquarePlus size={16} />
                      {t('nav.submitRequest')}
                    </Link>
                    <Link
                      to="/support"
                      style={supportDropdownItemStyle}
                      onClick={() => setSupportDropdownOpen(false)}
                    >
                      <List size={16} />
                      {t('nav.myRequests')}
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* User Info (desktop only) */}
            {!isMobile && user && (
              <div
                style={userNameStyle}
                onClick={() => navigate('/profile')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate('/profile');
                  }
                }}
                title="View profile"
              >
                <span
                  style={nameTextStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {user.firstName && user.lastName ? formatUserName(user.firstName, user.lastName) : user.email}
                </span>
                {isAdmin ? (
                  <ViewSwitcher
                    currentMode={viewMode}
                    onSwitch={(mode) => {
                      setViewMode(mode);
                      navigate('/'); // Navigate to home of new mode
                    }}
                  />
                ) : (
                  <span style={{ color: 'var(--text-secondary)' }}>({user.role === 'parent' ? t('roles.parent') : user.role})</span>
                )}
              </div>
            )}

            {/* Logout Button (desktop only) */}
            {!isMobile && (
              <button onClick={onLogout} style={logoutButtonStyle}>
                {t('logout')}
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
};
